import * as React from 'react'
import { StoreProps, castingStoreGetFirstEnabledDateFrom, castingStoreGetMaxDuration, castingStoreGetMinDuration, castingStoreGetMinStartDate, castingStoreGetMultiOpeningHours, castingStoreGetWorkables, castingStoreIsComplexDurationDifferentThanRangeAllowed, castingStoreIsStartDayDisabled } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { intersection, onlyUniqueByReference, onlyUniqueByTransform } from '../../../../services/utils/lodash.service'
import { moment } from '../../../../services/utils/moment.service'
import { openableGetComplexTimes } from '../../../../services/utils/openables.service'
import { getDayPickerDateLocale, StyledDayPicker } from '../../../shared/styled-day-picker.component'
import { CastingDayId, castingExtendsAllowedPatternOnDurationChanged, castingSlotAllowedPatternFirstDate } from '../../../../services/models/castings.service'
import { formatDurationFromSecs, formatTime, parseTime, utilRange } from '../../../../services/utils/utils.service'
import IconAdd from '../../../../../../../iconFont/src/add.svg'
import IconCloseAlternate from '../../../../../../../iconFont/src/close-alternate.svg'
import styled from 'styled-components'
import { FloatingContainerTitle, Link } from '../casting-styled.component'
import { Select } from '../../../shared/select.component'
import SlotAssignablePickerButton from '../assignables/slot-assignable-picker-button.component'
import CastingStartDateSkipWaitingOrderHoursPeriodComponent from '../start-date/casting-start-date-skip-waiting-order-hours-period.component'
import CastingFrequencyWeekIntervalPickerComponent from '../frequency/casting-frequency-week-interval-picker.component'
import { useCastingComplexMultiSlots } from './casting-complex-multi-slots.hook'
import { useCastingComplexMonoSlot } from './casting-complex-mono-slot.hook'
import StepperInputComponent from '../../../shared/stepper-input.component'

const SlotTable = styled.div`
  margin: 0.625rem 0;
  border-bottom: 1px solid ${ props => props.theme.colors.clr_divider };
`

const SlotHeader = styled.div`
  display: flex;
  flexWrap: wrap;
  margin: 0.625rem 0;
`

const SlotRow = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-bottom: 20px;
`

type Props = StoreProps & { hideFrequencyPicker?: boolean }
const i18nScoped = new I18nService(require('./casting-complex-content.component.yml'))

export default function CastingComplexContentComponent ({ data, store, hideFrequencyPicker }: Props) {
  const { offeredService, castingType, slots, backofficeSelectAssignableForSlots, complexVariantMonoSlotMultiAllowedPatterns } = data

  const labelPosition = React.useMemo(
    () => castingStoreIsComplexDurationDifferentThanRangeAllowed(data) || backofficeSelectAssignableForSlots ? 'top' : 'left',
    [backofficeSelectAssignableForSlots, data]
  )

  const multiOpeningHours = React.useMemo(() => castingStoreGetMultiOpeningHours(data).multiOpeningHours, [data])

  const selectedDays = React.useMemo(() => {
    return slots
      .flatMap(slot => slot.allowed_patterns.map(allowedPattern => castingSlotAllowedPatternFirstDate(allowedPattern.day_id, slot.start_date || new Date()).toDate()))
      .filter(onlyUniqueByTransform(date => date.getTime()))
  }, [slots])

  /** used for the oneshot complex mode, allowing the operator to pick a date in the past */
  const fromMonth = React.useMemo(
    () => castingStoreGetFirstEnabledDateFrom(castingStoreGetMinStartDate(data).toDate(), date => castingStoreIsStartDayDisabled(data, store, date, multiOpeningHours)),
    [data, multiOpeningHours, store]
  )

  const getComplexTimes = React.useCallback((dayId: CastingDayId, formattedStartDate = slots[0].start_date, isStart = true, startedAtHour: number = 0) => {
    if (!startedAtHour && castingType === 'casting_one_shot') {
      const minStartDate = castingStoreGetMinStartDate(data)
      const date = castingSlotAllowedPatternFirstDate(dayId, formattedStartDate)
      if (date.isSame(minStartDate, 'day')) startedAtHour = minStartDate.hours() + Math.ceil(minStartDate.minutes() / 60)
    }

    /**
     * When duration and range are uncorellated, the allowed patterns may be bigger or smaller than the requested duration (we use offeredService.duration_min as a minimum).
     * When a single slot is being created, the requested duration is set in the slot itself using an input at the top of the screen.
     * Otherwise, use castingStoreGetMinDuration()
     */
    const minDuration = castingStoreIsComplexDurationDifferentThanRangeAllowed(data)
      ? offeredService.duration_min
      : complexVariantMonoSlotMultiAllowedPatterns
      ? slots[0].duration
      : castingStoreGetMinDuration(data)

    return openableGetComplexTimes(multiOpeningHours, dayId === 0 ? 7 : dayId, minDuration, { durationStepSecs: offeredService.duration_step, isStart, startedAtHour })
  }, [castingType, complexVariantMonoSlotMultiAllowedPatterns, data, multiOpeningHours, offeredService.duration_min, offeredService.duration_step, slots])

  /** only check working days common to all workables, with large enough working hours to cover minimal duration. */
  const workingDays = React.useMemo(
    () => intersection(castingStoreGetWorkables(data).map(_ => _.working_days)).filter(workingDay => getComplexTimes(workingDay === 7 ? 0 : workingDay).length > 0),
    [data, getComplexTimes]
  )

  const isOneshotDateDisabled = React.useCallback((date: Date) => {
    if (castingStoreIsStartDayDisabled(data, store, date, multiOpeningHours)) return true

    // If mono slot, all allowed patterns share the same start_date, meaning that the offset relative to start_date is between 0 and 6 days.
    return complexVariantMonoSlotMultiAllowedPatterns && selectedDays.some(_ => Math.abs(_.getTime() - date.getTime()) >= 7 * 24 * 3600 * 1000)
  }, [complexVariantMonoSlotMultiAllowedPatterns, data, multiOpeningHours, selectedDays, store])
 
  const useComplex = complexVariantMonoSlotMultiAllowedPatterns ? useCastingComplexMonoSlot : useCastingComplexMultiSlots
  const { tableDataByDays, onComplexOneshotDayToggle, onComplexReccurringDayIdToggle, addPicker } = useComplex({ store, data, selectedDays, getComplexTimes })
  
  /**
   * When a single slot is created with multiple allowed patterns, a single duration input is required (displayed at the top of the screen).
   * When multiple slots are requested, each one may have a custom duration (global duration input is hidden in favor of per-row inputs).
   * 
   * In addition to those contraints, the duration is hidden when the service force a fixed duration per event (rentable_unit_of_work = 'event').
   */
  const showGlobalDurationSelector = complexVariantMonoSlotMultiAllowedPatterns && offeredService.rentable_unit_of_work != 'event'

  return <React.Fragment>
    { showGlobalDurationSelector && <div style={{ marginBottom: '1.25em' }}>
      { !offeredService.customer_asks_for_many_employees && <FloatingContainerTitle>{ i18nScoped.t('duration_in_hours') }</FloatingContainerTitle> }

      <StepperInputComponent
        name='duration' 
        value={ slots[0].duration }
        onChange={ duration => store.setSlots(castingExtendsAllowedPatternOnDurationChanged(slots, duration - slots[0].duration)) } 
        min={ store.getMinDuration() }
        max={ store.getMaxDuration() }
        step={ offeredService.duration_step }
        formatter={ formatDurationFromSecs } />
    </div> }

    { castingType === 'casting_one_shot' && <div className="u-mbm">
      <FloatingContainerTitle>{ i18nScoped.t('select_one_or_many_dates') }</FloatingContainerTitle>

      <CastingStartDateSkipWaitingOrderHoursPeriodComponent data={ data } store={ store } />

      <StyledDayPicker
        mode='multiple'
        disabled={ day => isOneshotDateDisabled(day) }
        selected={ selectedDays }
        locale={ getDayPickerDateLocale() }
        onDayClick={ onComplexOneshotDayToggle }
        startMonth={ fromMonth }
        onSelect={ () => undefined as any /* required for StyledDayPicker to enable the controlled input mode */ } />
    </div> }

    { castingType === 'casting_recurring' && <>
      { !hideFrequencyPicker && <CastingFrequencyWeekIntervalPickerComponent data={ data } store={ store } /> }

      <div className={ `selectableTable ${hideFrequencyPicker ? '' : 'u-mtm'}` }>
        <table>
          <thead><tr>{ workingDays.map(workingDay => <th key={ workingDay }>{ moment().day(workingDay).format('dddd') }</th>) }</tr></thead>
          <tbody>
            <tr>
              { workingDays.map(workingDay => <td key={ workingDay }>
                <div className="inputRadio inputRadio--full">
                  <input
                    type="checkbox"
                    id={ `timespanTable-${workingDay}` }
                    checked={
                      complexVariantMonoSlotMultiAllowedPatterns
                        ? slots[0].allowed_patterns.some(allowedPattern => allowedPattern.day_id === workingDay % 7)
                        : slots.some(slot => slot.allowed_patterns.length === 1 && slot.allowed_patterns[0].day_id === workingDay % 7)
                    }
                    onChange={ () => onComplexReccurringDayIdToggle(workingDay === 7 ? 0 : workingDay) } />
                  <label htmlFor={ `timespanTable-${workingDay}` }></label>
                </div>
              </td>) }
            </tr>
          </tbody>
        </table>
      </div>
    </> }

    { tableDataByDays.map((tableDataByDay, index) => <SlotTable key={ tableDataByDay[0].startDate.format() }>
      <SlotHeader id={ `js-test-slot-header-${tableDataByDay[0].startDate.format('YYYY-MM-DD')}` }>
        <span style={{ textTransform: 'capitalize' }}>{ tableDataByDay[0].startDate.format(castingType === 'casting_recurring' ? 'dddd' : 'dddd LL') }</span>
        <span style={{ flexGrow: 1 }}></span>
        <Link onClick={ () => addPicker(tableDataByDay[0].dayId, castingType === 'casting_one_shot' ? tableDataByDay[0].startDate.format('YYYY-MM-DD') : undefined) } style={{ fontWeight: 'bold' }}>
          { i18nScoped.t(complexVariantMonoSlotMultiAllowedPatterns ? 'add_allowed_pattern' : 'add_slot') } <IconAdd height="1em" style={{ marginLeft: '5px' }} />
        </Link>
      </SlotHeader>

      { tableDataByDay.map((tableData, index2) => {
        return <SlotRow key={ index2 }>
          { offeredService.rentable_unit_of_work !== 'event' && <React.Fragment>
            { labelPosition === 'left' && <div>{ i18nScoped.t('from') }</div> }
            <div>
              { labelPosition === 'top' && <div>{ i18nScoped.t('from') }</div> }

              <Select
                id={ `js-test-complex-slot-from-${index}-${index2}` }
                styles={{ container: base => ({ ...base, width: 90 }), indicatorSeparator: () => ({ display: 'none' }) }}
                value={{ value: tableData.fromHour, label: tableData.fromHour }}
                options={
                  getComplexTimes(tableData.dayId, tableData.startDate.format('YYYY-MM-DD'))
                    .concat(tableData.fromHour ? [parseTime(tableData.fromHour)] : [])
                    .filter(onlyUniqueByReference)
                    .map(formatTime)
                    .map(value => ({ value, label: value }))
                }
                onChange={ val => tableData.setFromHour(val.value) }
                menuPlacement="top"
                isSearchable={ false } />
            </div>

            { labelPosition === 'left' && <div>{ i18nScoped.t('to') }</div> }
            <div>
              { labelPosition === 'top' && <div>{ i18nScoped.t('to') }</div> }

              { /**
               * If multiple slots are requested, the Select until_time input is always visible (but the duration input may be hidden according to (castingStoreIsComplexDurationDifferentThanRangeAllowed)).
               * On the other hand, the until_time input is hidden when a single slot is being requested, because its duration is set using the global duration input at the top of the screen.
               * */ }
              { (castingStoreIsComplexDurationDifferentThanRangeAllowed(data) || !complexVariantMonoSlotMultiAllowedPatterns) ? <div>
                <Select
                  id={ `js-test-complex-slot-until-${index}-${index2}` }
                  styles={{ container: base => ({ ...base, width: 90 }), indicatorSeparator: () => ({ display: 'none' }) }}
                  value={{ value: tableData.untilHour, label: tableData.untilHour }}
                  options={
                    getComplexTimes(tableData.dayId, tableData.startDate.format('YYYY-MM-DD'), false, parseTime(tableData.fromHour))
                      .concat(tableData.untilHour ? [parseTime(tableData.untilHour)] : [])
                      .filter(onlyUniqueByReference)
                      .map(formatTime)
                      .map(value => ({ value, label: value }))
                  }
                  onChange={ val => tableData.setUntilHour(val.value) }
                  menuPlacement="top"
                  isSearchable={ false } />
              </div>: <div id={ `js-test-complex-slot-readonly-until-time-${index}-${index2}` }>
                { tableData.untilHour }
              </div> }
            </div>

            { !backofficeSelectAssignableForSlots && isMultiSlots(tableData) && (
              castingStoreIsComplexDurationDifferentThanRangeAllowed(data) ? <div>
                <div>{ i18nScoped.t('duration') }</div>

                <Select
                  id={ `js-test-complex-slot-duration-${index}-${index2}` }
                  styles={{ container: base => ({ ...base, width: 90 }), indicatorSeparator: () => ({ display: 'none' }) }}
                  value={{ value: tableData.slot.duration }}
                  options={ utilRange(castingStoreGetMinDuration(data), castingStoreGetMaxDuration(data) + 0.01, offeredService.duration_step).map(value => ({ value })) }
                  getOptionLabel={ val => formatDurationFromSecs(val.value, { hideMinIfHours: true, truncSpaces: true, showZeroMinutes: true }) }
                  onChange={ val => tableData.setDuration(val.value) }
                  menuPlacement="top"
                  isSearchable={ false } />
              </div> : <div id={ `js-test-complex-slot-readonly-duration-${index2}` }>
                { formatDurationFromSecs(tableData.slot.duration, { hideMinIfHours: true, truncSpaces: true, showZeroMinutes: true }) }
              </div>
            ) }
          </React.Fragment> }
          
          { offeredService.rentable_unit_of_work === 'event' && <div style={{ flexGrow: 1 }}>
            { backofficeSelectAssignableForSlots && <div>{ i18nScoped.t('slot') }</div> }

            <Select
              id={ `js-test-complex-slot-flat-rate-${index}-${index2}` }
              options={ getComplexTimes(tableData.dayId, tableData.startDate.format('YYYY-MM-DD')).map(value => ({ value })) }
              styles={{ container: base => ({ ...base, width: '95%' }), indicatorSeparator: () => ({ display: 'none' }) }}
              value={{ value: parseTime(tableData.fromHour) }}
              getOptionLabel={ val => `${formatTime(val.value)} - ${formatTime(val.value + castingStoreGetMinDuration(data) / 3600)} (${i18nScoped.t('flat_rate_duration', { duration: formatDurationFromSecs(castingStoreGetMinDuration(data)) })})` }
              onChange={ val => tableData.setAllowedPattern({ day_id: tableData.dayId, from_hour: formatTime(val.value), until_hour: formatTime(val.value + castingStoreGetMinDuration(data) / 3600), additional_days: null }) }
              menuPlacement="top"
              isSearchable={ false } />
          </div> }

          { backofficeSelectAssignableForSlots && isMultiSlots(tableData) && <div>
            <SlotAssignablePickerButton store={ store } data={ data } slot={ tableData.slot } />
          </div> }

          <IconCloseAlternate height="1em" onClick={ () => tableData.remove() } />
        </SlotRow>
      }) }
    </SlotTable>) }
  </React.Fragment>
}

function isMultiSlots (tableData: any): tableData is ReturnType<typeof useCastingComplexMultiSlots>['tableDataByDays'][0][0] {
  return 'slot' in tableData
}
